/**
 * API 遷移助手
 * 幫助舊的 API 調用平滑過渡到新的 RBAC 系統
 */

import { useAuth } from '../context/AuthContext';
import axios from './axiosConfig';
import { isSuperAdmin } from './superAdmin';

// 用戶角色判斷
function getUserRole(user) {
  if (!user) return 'guest';
  
  const { e_id, e_position } = user;
  
  if (isSuperAdmin(e_id)) return 'super_admin';
  if (parseInt(e_position) === 17) return 'secretary';
  return 'member';
}

/**
 * API 映射表 - 將舊 API 映射到新的 RBAC API
 * 🚧 暫時停用 RBAC API，因為後端尚未實作
 */
const apiMappings = {
  // 🚧 RBAC API 暫時停用，等待後端實作
  // 員工相關 API
  // '/Allemployees': {
  //   member: '/api/employees/member/colleagues',
  //   secretary: '/api/employees/secretary/manage',
  //   super_admin: '/api/employees/admin/system-wide'
  // },

  // '/employees': {
  //   member: '/api/employees/member/colleagues',
  //   secretary: '/api/employees/secretary/manage',
  //   super_admin: '/api/employees/admin/system-wide'
  // },

  // 活動相關 API
  // '/activities': {
  //   member: '/api/activities/member/available',
  //   secretary: '/api/activities/secretary/manage',
  //   super_admin: '/api/activities/admin/system-wide'
  // }
};

/**
 * 參數轉換器
 * 將舊 API 的參數格式轉換為新 API 的格式
 */
const parameterTransformers = {
  // Allemployees 參數轉換
  '/Allemployees': {
    member: (params) => ({
      page: params.page || 1,
      limit: params.limit || 20,
      search: params.store_name || params.search || ''
    }),
    secretary: (params) => ({
      page: params.page || 1,
      limit: params.limit || 50,
      search: params.search || '',
      includeInactive: params.allData === 'true'
    }),
    super_admin: (params) => ({
      page: params.page || 1,
      limit: params.limit || 100,
      search: params.search || '',
      storeId: params.store_name ? '' : undefined, // 需要根據 store_name 查找 storeId
      includeInactive: params.allData === 'true'
    })
  },
  
  // activities 參數轉換
  '/activities': {
    member: (params) => ({
      page: params.page || 1,
      limit: params.limit || 10,
      search: params.filter || ''
    }),
    secretary: (params) => ({
      page: params.page || 1,
      limit: params.limit || 20,
      search: params.filter || '',
      includeHistory: params.self === 'true',
      status: 'all'
    }),
    super_admin: (params) => ({
      page: params.page || 1,
      limit: params.limit || 50,
      search: params.filter || '',
      storeId: params.e_ssid || '',
      status: 'all',
      dateRange: 'all'
    })
  }
};

/**
 * 回應資料轉換器
 * 將新 API 的回應格式轉換為舊 API 相容的格式
 */
const responseTransformers = {
  '/Allemployees': (newData, originalParams) => {
    // 新 API 回應格式轉換為舊格式
    if (newData.success) {
      return {
        data: {
          data: newData.data,
          pagination: newData.pagination,
          total: newData.pagination?.total || newData.data.length
        }
      };
    }
    return newData;
  },
  
  '/activities': (newData, originalParams) => {
    if (newData.success) {
      return {
        data: {
          activities: newData.data,
          pagination: newData.pagination,
          hasMore: newData.pagination ? 
            (newData.pagination.page * newData.pagination.limit) < newData.pagination.total : 
            false
        }
      };
    }
    return newData;
  }
};

/**
 * 智能 API 調用器
 * 根據用戶角色自動選擇合適的新 API
 */
export class SmartApiClient {
  constructor() {
    this.user = null;
    this.token = localStorage.getItem('token');
  }
  
  setUser(user) {
    this.user = user;
  }
  
  setToken(token) {
    this.token = token;
  }
  
  /**
   * 智能 API 調用
   * @param {string} originalEndpoint - 原始端點
   * @param {object} params - 參數
   * @param {object} options - 額外選項
   */
  async smartCall(originalEndpoint, params = {}, options = {}) {
    const userRole = getUserRole(this.user);
    
    // 檢查是否有對應的新 API
    const mapping = apiMappings[originalEndpoint];
    if (!mapping) {
      // 沒有映射，使用原始 API
      console.warn(`No RBAC mapping for ${originalEndpoint}, using original API`);
      return this.callOriginalAPI(originalEndpoint, params, options);
    }
    
    // 獲取對應角色的新端點
    const newEndpoint = mapping[userRole];
    if (!newEndpoint) {
      // 角色沒有權限，返回空結果
      console.warn(`No permission for role ${userRole} on ${originalEndpoint}`);
      return { data: { data: [], total: 0 } };
    }
    
    try {
      // 轉換參數
      const transformer = parameterTransformers[originalEndpoint];
      const transformedParams = transformer && transformer[userRole] 
        ? transformer[userRole](params)
        : params;
      
      console.log(`🔄 API Migration: ${originalEndpoint} → ${newEndpoint}`, {
        originalParams: params,
        transformedParams,
        userRole
      });
      
      // 調用新 API
      const response = await axios.get(newEndpoint, {
        params: transformedParams,
        headers: { Authorization: `Bearer ${this.token}` },
        ...options
      });
      
      // 轉換回應
      const responseTransformer = responseTransformers[originalEndpoint];
      const transformedResponse = responseTransformer 
        ? responseTransformer(response.data, params)
        : response;
      
      return transformedResponse;
      
    } catch (error) {
      console.error(`RBAC API call failed, falling back to original API:`, error);
      // 新 API 失敗，降級到原始 API
      return this.callOriginalAPI(originalEndpoint, params, options);
    }
  }
  
  /**
   * 調用原始 API（降級方案）
   */
  async callOriginalAPI(endpoint, params = {}, options = {}) {
    try {
      const response = await axios.get(endpoint, {
        params,
        headers: { Authorization: `Bearer ${this.token}` },
        ...options
      });
      return response;
    } catch (error) {
      console.error(`Original API call failed:`, error);
      throw error;
    }
  }
}

// 單例實例
const smartApiClient = new SmartApiClient();

/**
 * React Hook for Smart API
 */
export function useSmartApi() {
  const { user, token } = useAuth();
  
  
  // 更新用戶和 token
  if (user) smartApiClient.setUser(user);
  if (token) smartApiClient.setToken(token);
  
  return {
    smartCall: (endpoint, params, options) => 
      smartApiClient.smartCall(endpoint, params, options),
    
    // 便利方法
    getEmployees: (params = {}) => 
      smartApiClient.smartCall('/Allemployees', params),
    
    getActivities: (params = {}) => 
      smartApiClient.smartCall('/activities', params),
    
    // 獲取用戶角色
    getUserRole: () => getUserRole(user),
    
    // 檢查是否支援智能調用
    hasRBACMapping: (endpoint) => !!apiMappings[endpoint]
  };
}

export default smartApiClient;